import { defineStore } from "pinia"
import {
  BenefitDependentTypeEnum,
  BenefitTypeEnum,
  CompanyBenefitPlanDTO,
  CompanyBenefitPlanTierCostDTO,
  EnrollDependentIntoCompanyBenefitRequest,
  PendingCompanyBenefitEnrollmentDTO
} from "@vitable/asclepius-api-client-ts"
import { useListPendingCompanyBenefitEnrollments } from "@/composables/benefits-enrollment"
import { useUserStore } from "@/store/user-store"

export enum BenefitFlowType {
  VPC_FAMILY = "VPC_FAMILY",
  VPC_FAMILY_PLUS = "VPC_FAMILY_PLUS",
  VPC_AND_MEC = "VPC_AND_MEC",
  VPC_AND_MEC2 = "VPC_AND_MEC2",
  MEC2_ONLY = "MEC2_ONLY"
}

export interface EnrollmentStore {
  pendingCompanyBenefitEnrollments: Record<string, Array<PendingCompanyBenefitEnrollmentDTO>>

  currPendingEnrollmentId: Optional<string>
  currPendingEnrollment: Optional<PendingCompanyBenefitEnrollmentDTO>

  currPendingEnrollmentHasInsuranceCoverage: Optional<boolean>
  currPendingEnrollmentSelectedPlan: Optional<CompanyBenefitPlanDTO>
  currPendingEnrollmentSelectedPlanTiers: Array<CompanyBenefitPlanTierCostDTO>
  currPendingEnrollmentAddedDependents: Array<EnrollDependentIntoCompanyBenefitRequest>
}

export const useEnrollmentStore = defineStore("enrollmentNewStore", {
  persist: true,
  state: (): EnrollmentStore => {
    return {
      pendingCompanyBenefitEnrollments: {},

      currPendingEnrollmentId: undefined,
      currPendingEnrollment: undefined,

      currPendingEnrollmentHasInsuranceCoverage: undefined,
      currPendingEnrollmentSelectedPlan: undefined,
      currPendingEnrollmentSelectedPlanTiers: [],
      currPendingEnrollmentAddedDependents: []
    }
  },
  getters: {
    pendingCompanyBenefitEnrollmentsForHomePage(): Array<PendingCompanyBenefitEnrollmentDTO> {
      const pendingCompanyBenefitEnrollmentsForHomePage: Array<PendingCompanyBenefitEnrollmentDTO> = []

      for (const companyId in this.pendingCompanyBenefitEnrollments) {
        const pendingCompanyBenefits = this.pendingCompanyBenefitEnrollments[companyId]
        const hasMEC2Benefit = pendingCompanyBenefits.some(
          (enrollment) => enrollment.benefit_type === BenefitTypeEnum.Mec2
        )

        for (const pendingEnrollment of pendingCompanyBenefits) {
          if (pendingEnrollment.benefit_type === BenefitTypeEnum.Vpc && hasMEC2Benefit) {
            // Skip VPC benefits if there is an MEC2 benefit
            continue
          }
          pendingCompanyBenefitEnrollmentsForHomePage.push(pendingEnrollment)
        }
      }

      return pendingCompanyBenefitEnrollmentsForHomePage
    }
  },
  actions: {
    // Load all pending company benefit enrollments for member
    async loadPendingCompanyBenefitEnrollments() {
      const userStore = useUserStore()
      const memberId: string = userStore.memberId!

      const { pendingCompanyBenefitEnrollments, listPendingCompanyBenefitEnrollments } =
        useListPendingCompanyBenefitEnrollments()

      await listPendingCompanyBenefitEnrollments(memberId)

      // Initialize the map
      const enrollmentMap: Record<string, Array<PendingCompanyBenefitEnrollmentDTO>> = {}

      // Populate the map
      pendingCompanyBenefitEnrollments.value.forEach((enrollment) => {
        const companyId = enrollment.company_id
        if (!enrollmentMap[companyId]) {
          enrollmentMap[companyId] = []
        }
        enrollmentMap[companyId].push(enrollment)
      })

      this.pendingCompanyBenefitEnrollments = enrollmentMap
    },

    setCurrPendingEnrollmentId(currPendingEnrollmentId: Optional<string>) {
      this.clearPendingEnrollment()
      this.currPendingEnrollmentId = currPendingEnrollmentId
    },

    async loadCurrPendingEnrollment(): Promise<boolean> {
      if (!this.currPendingEnrollmentId) {
        this.currPendingEnrollment = undefined
        return false
      }

      await this.loadPendingCompanyBenefitEnrollments()

      for (const companyId in this.pendingCompanyBenefitEnrollments) {
        const pendingEnrollment = this.pendingCompanyBenefitEnrollments[companyId].find(
          (pendingCompanyBenefitEnrollment) => pendingCompanyBenefitEnrollment.id === this.currPendingEnrollmentId
        )
        if (pendingEnrollment) {
          this.currPendingEnrollment = pendingEnrollment
          return true
        }
      }

      this.currPendingEnrollment = undefined
      return false
    },

    setHasInsuranceCoverage(hasInsuranceCoverage: boolean) {
      this.currPendingEnrollmentHasInsuranceCoverage = hasInsuranceCoverage
    },

    getPendingVPCBenefitPlanByCompanyId(companyId: string): CompanyBenefitPlanDTO | undefined {
      const pendingCompanyBenefits = this.pendingCompanyBenefitEnrollments[companyId]
      if (!pendingCompanyBenefits) return undefined

      for (const pendingCompanyBenefitEnrollment of pendingCompanyBenefits) {
        for (const companyBenefitPlan of pendingCompanyBenefitEnrollment.benefit_plans) {
          if (companyBenefitPlan.vitable_only_in) {
            return companyBenefitPlan
          }
        }
      }
      return undefined
    },

    getBenefitPlanByTierCost(tierCost: CompanyBenefitPlanTierCostDTO | undefined): CompanyBenefitPlanDTO | null {
      if (!tierCost) return null

      const enrollmentStore = useEnrollmentStore()

      for (const companyId in enrollmentStore.pendingCompanyBenefitEnrollments) {
        const enrollments = enrollmentStore.pendingCompanyBenefitEnrollments[companyId]
        for (const enrollment of enrollments) {
          const foundPlan = enrollment.benefit_plans.find((plan) => plan.id === tierCost.benefit_plan_id)
          if (foundPlan) {
            return foundPlan
          }
        }
      }

      return null
    },

    getBenefitEnrollmentByTierCost(
      tierCost: CompanyBenefitPlanTierCostDTO,
      companyId: string
    ): PendingCompanyBenefitEnrollmentDTO | null {
      const enrollmentStore = useEnrollmentStore()
      const enrollments = enrollmentStore.pendingCompanyBenefitEnrollments[companyId]

      if (!enrollments) return null

      for (const enrollment of enrollments) {
        const foundPlan = enrollment.benefit_plans.find(
          (plan) => plan.id === tierCost.benefit_plan_id && plan.company_id === companyId
        )
        if (foundPlan) {
          return enrollment
        }
      }

      return null
    },

    setSelectedPlan(selectedPlan: CompanyBenefitPlanDTO) {
      this.currPendingEnrollmentSelectedPlan = selectedPlan
    },

    setSelectedTiers(selectedTiers: Array<CompanyBenefitPlanTierCostDTO>) {
      this.currPendingEnrollmentSelectedPlanTiers = []
      this.currPendingEnrollmentSelectedPlanTiers = selectedTiers
    },

    addDependentToEnrollmentRequest(addedDependent: EnrollDependentIntoCompanyBenefitRequest) {
      if (addedDependent.type === BenefitDependentTypeEnum.Spouse) {
        const isSpouseExist = this.currPendingEnrollmentAddedDependents.findIndex(
          (d) => d.type === BenefitDependentTypeEnum.Spouse
        )
        if (isSpouseExist === -1) {
          this.currPendingEnrollmentAddedDependents.push(addedDependent)
        } else {
          throw new Error("You can only add one spouse")
        }
      } else {
        this.currPendingEnrollmentAddedDependents.push(addedDependent)
      }
    },

    removeDependentFromEnrollmentRequest(dependent: EnrollDependentIntoCompanyBenefitRequest) {
      const index = this.currPendingEnrollmentAddedDependents.findIndex(
        (d) =>
          d.first_name === dependent.first_name &&
          d.last_name === dependent.last_name &&
          d.date_of_birth === dependent.date_of_birth &&
          d.type === dependent.type
      )
      if (index !== -1) {
        this.currPendingEnrollmentAddedDependents.splice(index, 1)
      }
    },

    clearPendingEnrollment() {
      this.currPendingEnrollment = undefined
      this.currPendingEnrollmentId = undefined
      this.currPendingEnrollmentHasInsuranceCoverage = undefined
      this.currPendingEnrollmentSelectedPlanTiers = []
      this.currPendingEnrollmentAddedDependents = []
    },

    computeBenefitFlowType(): BenefitFlowType | undefined {
      if (!this.currPendingEnrollment) {
        return undefined
      }

      const currentCompanyId = this.currPendingEnrollment.company_id
      const pendingCompanyBenefits = this.pendingCompanyBenefitEnrollments[currentCompanyId]

      if (!pendingCompanyBenefits) {
        return undefined
      }

      const hasVPCFamilyPlus = pendingCompanyBenefits.some(
        (enrollment) => enrollment.benefit_type === BenefitTypeEnum.Vpc
      )
      const hasVPCFamily = pendingCompanyBenefits.some(
        (enrollment) => enrollment.benefit_type === BenefitTypeEnum.VpcFamily
      )
      const hasMEC = pendingCompanyBenefits.some((enrollment) => enrollment.benefit_type === BenefitTypeEnum.Mec)
      const hasMEC2 = pendingCompanyBenefits.some((enrollment) => enrollment.benefit_type === BenefitTypeEnum.Mec2)

      if (hasMEC2) {
        return hasVPCFamilyPlus ? BenefitFlowType.VPC_AND_MEC2 : BenefitFlowType.MEC2_ONLY
      } else if (hasMEC) {
        return BenefitFlowType.VPC_AND_MEC
      } else if (hasVPCFamilyPlus) {
        return BenefitFlowType.VPC_FAMILY_PLUS
      } else if (hasVPCFamily) {
        return BenefitFlowType.VPC_FAMILY
      }

      return undefined
    }
  }
})
